const CHUNK_ERROR_FRAGMENTS = [
  'Failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'Importing a module script failed',
  'Failed to load module script',
] as const;

/**
 * sessionStorage key guarding against infinite reload loops: a stale-cache
 * chunk failure triggers at most one reload per page-load session. The guard is
 * re-armed only on a fresh navigation (see resetReloadGuardForFreshNavigation),
 * not on individual successful imports.
 */
export const CHUNK_RELOAD_FLAG = 'bd-chunk-reload-attempted';

/**
 * Detects the browser errors thrown when a lazily imported chunk can no longer
 * be fetched, which typically happens after a deploy invalidates the hashed
 * filenames a stale client still references.
 * @param error - The value thrown by a failed dynamic import
 * @returns True when the error looks like a chunk load failure
 */
export const isChunkError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : '';
  return CHUNK_ERROR_FRAGMENTS.some((fragment) => message.includes(fragment));
};

/**
 * Whether a stale-cache reload has already been triggered this session.
 * @returns True only when the guard flag is present and readable
 */
export const hasReloadAttempt = (): boolean => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_FLAG) === 'true';
  } catch (error) {
    console.warn(
      'Unable to read chunk reload guard from sessionStorage',
      error
    );
    return false;
  }
};

/**
 * Persists the one-reload-per-session guard and reports whether it stuck.
 *
 * This is deliberately fail-safe: callers must only reload when this returns
 * true. If sessionStorage is unavailable (e.g. Safari Lockdown mode, blocked
 * site data) the write fails, we cannot remember that we reloaded, and reloading
 * anyway would loop forever — so callers fall through to the error boundary
 * instead.
 * @returns True when the guard was durably written, false otherwise
 */
export const markReloadAttempt = (): boolean => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_FLAG, 'true');
    return true;
  } catch (error) {
    console.warn('Unable to write chunk reload guard to sessionStorage', error);
    return false;
  }
};

/**
 * Clears the reload guard so a future stale-cache event can reload again.
 */
export const clearReloadAttempt = (): void => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch (error) {
    console.warn(
      'Unable to clear chunk reload guard from sessionStorage',
      error
    );
  }
};

/**
 * Re-arms the one-reload-per-session guard on a genuine page load, but never on
 * our own recovery reload.
 *
 * Call this once during app bootstrap. We must NOT clear the guard on the reload
 * we triggered ourselves: the failing chunk is retried after that reload, and
 * clearing first would let the retry reload again — an infinite loop. Using the
 * navigation type lets us clear only on a fresh load or back/forward, so each
 * page-load session gets exactly one reload budget.
 *
 * This is fail-safe like the storage guard: we only re-arm when we positively
 * know this is a non-reload navigation. If the navigation type is unavailable
 * (no timing entry, unsupported), we keep the guard rather than risk clearing it
 * on our own recovery reload and looping.
 */
export const resetReloadGuardForFreshNavigation = (): void => {
  try {
    const [navigationEntry] = performance.getEntriesByType(
      'navigation'
    ) as PerformanceNavigationTiming[];
    const navigationType = navigationEntry?.type;
    // Re-arm only for a known non-reload navigation; keep the guard otherwise.
    if (navigationType && navigationType !== 'reload') {
      clearReloadAttempt();
    }
  } catch (error) {
    console.warn('Unable to reset chunk reload guard', error);
  }
};

/**
 * Wraps a dynamic import factory so a stale-cache chunk failure forces a
 * one-time full page reload, which re-fetches the current chunk manifest.
 *
 * The reload is guarded by a sessionStorage flag so a chunk that is genuinely
 * gone (rather than merely stale) cannot trap the app in a reload loop: the
 * second failure rethrows and surfaces to the nearest error boundary instead.
 * If the guard cannot be persisted (storage unavailable) we skip the reload
 * entirely rather than risk an unguarded loop. Non-chunk errors are always
 * rethrown untouched.
 *
 * The guard is intentionally NOT cleared on a successful import: doing so per
 * import would re-arm it mid-session and let a second, genuinely-gone chunk on
 * the same route trigger another reload. Instead it is re-armed once per fresh
 * page load via resetReloadGuardForFreshNavigation, giving one reload budget per
 * page-load session.
 * @param importFn - The `() => import(...)` factory to protect
 * @returns A factory with the same signature that reloads on chunk failure
 */
export const retryDynamicImport = <T>(
  importFn: () => Promise<T>
): (() => Promise<T>) => {
  return async () => {
    try {
      return await importFn();
    } catch (error) {
      // Only reload when we can persist the guard (markReloadAttempt returns
      // true); otherwise an unavailable sessionStorage would loop forever.
      if (isChunkError(error) && !hasReloadAttempt() && markReloadAttempt()) {
        window.location.reload();
        // The reload replaces the document, so this promise intentionally never
        // settles; returning it keeps React.lazy suspended until then.
        return new Promise<T>(() => {});
      }
      throw error;
    }
  };
};
